import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Web App",
  "Mobile App",
  "AI Integration",
  "Custom SaaS",
  "CRM / Internal Tool",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Name, email, phone and a brief message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Transmission received. We'll reply within 48 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050505] py-32 md:py-48 border-t border-white/[0.06]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/50 mb-8">
              (05) — Start a project
            </div>
            <h2 className="font-display font-extrabold uppercase leading-[0.85] tracking-tighter text-5xl md:text-6xl lg:text-7xl">
              Let's build<br />
              <span className="text-white/40">something</span><br />
              ridiculous.
            </h2>

            <div className="mt-16 space-y-6 font-mono-accent text-xs uppercase tracking-[0.25em] text-white/60">
              <div>
                <div className="text-white/40 mb-1">Email</div>
                <a href="mailto:contact@nanstudio.al" className="text-white hover:underline">
                  contact@nanstudio.al
                </a>
              </div>
              <div>
                <div className="text-white/40 mb-1">Studio</div>
                <div>Tirana, Albania</div>
              </div>
              <div>
                <div className="text-white/40 mb-1">Hours</div>
                <div>Mon – Fri / 09:00 – 19:00 CET</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <motion.form
              data-testid="contact-form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="border border-white/[0.06] bg-[#0a0a0a] p-8 md:p-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                <Field
                  label="Your name *"
                  value={form.name}
                  onChange={update("name")}
                  testid="contact-input-name"
                  placeholder="Your Name"
                />
                <Field
                  label="Email *"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  testid="contact-input-email"
                  placeholder="you@company.com"
                />
                <Field
                  label="Phone *"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  testid="contact-input-phone"
                  placeholder="+1 555 000 0000"
                />
                <Field
                  label="Company"
                  value={form.company}
                  onChange={update("company")}
                  testid="contact-input-company"
                  placeholder="(Optional)"
                />
                <SelectField
                  label="Service"
                  value={form.service}
                  onChange={update("service")}
                  options={services}
                  testid="contact-input-service"
                />
                <div />
                <div className="md:col-span-2">
                  <Field
                    label="Tell us about the project"
                    value={form.message}
                    onChange={update("message")}
                    testid="contact-input-message"
                    placeholder="What are you building, who is it for, when do you need it?"
                    multiline
                  />
                </div>
              </div>

              <div className="mt-12 flex items-center justify-between gap-6">
                <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40">
                  Reply within 48h · NDA on request
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#29ABE2] text-black font-medium text-sm hover:bg-[#5cc2ec] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Transmitting…" : done ? "Sent ✓ Send another?" : "Send transmission"}
                  <Send
                    size={14}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testid, placeholder, type = "text", multiline }) {
  return (
    <label className="block group">
      <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
        {label}
      </div>
      {multiline ? (
        <textarea
          data-testid={testid}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={5}
          className="w-full bg-transparent border-0 border-b border-white/[0.12] focus:border-white py-2 text-white placeholder:text-white/25 outline-none transition-colors resize-none text-base"
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full bg-transparent border-0 border-b border-white/[0.12] focus:border-white py-2 text-white placeholder:text-white/25 outline-none transition-colors text-base"
        />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
        {label}
      </div>
      <div className="relative">
        <select
          data-testid={testid}
          value={value}
          onChange={onChange}
          className="w-full appearance-none bg-transparent border-0 border-b border-white/[0.12] focus:border-white py-2 pr-8 text-white outline-none cursor-pointer text-base"
        >
          <option value="" className="bg-[#0a0a0a] text-white/60">
            Select…
          </option>
          {options.map((o) => (
            <option key={o} value={o} className="bg-[#0a0a0a] text-white">
              {o}
            </option>
          ))}
        </select>
        <ArrowUpRight
          size={14}
          className="absolute right-1 top-3 text-white/40 pointer-events-none"
        />
      </div>
    </label>
  );
}
